import React, { useMemo, useState, useEffect, useCallback } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import TableLoader from "../TableLoader";
import InvoiceChart from "./graph/InvoiceChart";
import "./report.scss";
import Apiauth from "../../../API/Apiauth";
import { toFormatPrice } from "../helpers/function";
import { useTranslation } from "react-i18next";

function formatNum(n) {
  if (n == null || n === "") return "";
  return Number(n).toLocaleString();
}

function monthsArrayToByMonthIndex(columnMonths, rowMonths) {
  const byKey = new Map();
  for (const m of rowMonths || []) {
    byKey.set(m.key, {
      invoices: Number(m.invoice) || 0,
      qty: Number(m.qty) || 0,
      sales: Number(m.sales) || 0,
    });
  }
  const byMonth = {};
  (columnMonths || []).forEach((col, i) => {
    const cell = byKey.get(col.key);
    byMonth[i] = cell || { invoices: 0, qty: 0, sales: 0 };
  });
  return byMonth;
}

function sumMappedGroupsByMonth(groups, columnCount) {
  const byMonth = {};
  for (let i = 0; i < columnCount; i += 1) {
    byMonth[i] = { invoices: 0, qty: 0, sales: 0 };
  }
  for (const g of groups || []) {
    for (let i = 0; i < columnCount; i += 1) {
      const m = g.byMonth[i];
      if (!m) continue;
      byMonth[i].invoices += m.invoices;
      byMonth[i].qty += m.qty;
      byMonth[i].sales += m.sales;
    }
  }
  return byMonth;
}

function sumNetQtyAndSalesByMonth(groups, columnCount) {
  const salesByMonth = {};
  const refundByMonth = {};
  for (let i = 0; i < columnCount; i += 1) {
    salesByMonth[i] = { qty: 0, sales: 0 };
    refundByMonth[i] = { qty: 0, sales: 0 };
  }

  for (const g of groups || []) {
    const isRefundGroup = /exchange|refund|credit/i.test(String(g?.label || ""));
    for (let i = 0; i < columnCount; i += 1) {
      const m = g?.byMonth?.[i];
      if (!m) continue;
      if (isRefundGroup) {
        refundByMonth[i].qty += Number(m.qty) || 0;
        refundByMonth[i].sales += Number(m.sales) || 0;
      } else {
        salesByMonth[i].qty += Number(m.qty) || 0;
        salesByMonth[i].sales += Number(m.sales) || 0;
      }
    }
  }

  const netByMonth = {};
  for (let i = 0; i < columnCount; i += 1) {
    netByMonth[i] = {
      qty: (salesByMonth[i]?.qty || 0) - (refundByMonth[i]?.qty || 0),
      sales: (salesByMonth[i]?.sales || 0) - (refundByMonth[i]?.sales || 0),
    };
  }
  return netByMonth;
}

function mapApiGroups(columnMonths, data) {
  return (data || []).map((row) => ({
    id: `g-${row.no}`,
    index: row.no,
    label: row.type,
    byMonth: monthsArrayToByMonthIndex(columnMonths, row.months),
    children: (row.children || []).map((c) => ({
      id: c.location_id,
      label: c.location_Name,
      byMonth: monthsArrayToByMonthIndex(columnMonths, c.months),
    })),
  }));
}

function mapMetricsFromSummary(summary) {
  if (!summary || typeof summary !== "object") {
    return [
      { label: "Invoice", value: "-" },
      { label: "Exchange & Refund", value: "-" },
      { label: "Qty", value: "-" },
      { label: "Sales", value: "-" },
    ];
  }
  return [
    { label: "Invoice", value: formatNum(summary.invoice) },
    { label: "Exchange & Refund", value: formatNum(summary.exchange_refund) },
    { label: "Qty", value: formatNum(summary.qty) },
    { label: "Sales", value: toFormatPrice(Number(summary.sales || 0)) },
  ];
}

function MonthCells({ byMonth, columnCount }) {
  const cells = [];
  for (let mi = 0; mi < columnCount; mi += 1) {
    const m = byMonth[mi] ?? { invoices: 0, qty: 0, sales: 0 };
    cells.push(
      <React.Fragment key={mi}>
        <td className="invoice-report-td invoice-report-td--num">{formatNum(m.invoices)}</td>
        <td className="invoice-report-td invoice-report-td--num">{formatNum(m.qty)}</td>
        <td className="invoice-report-td invoice-report-td--num">
          {toFormatPrice(Number(m.sales || 0))}
        </td>
      </React.Fragment>
    );
  }
  return cells;
}

const Invoice = () => {
   const { t } = useTranslation()
  const [columnMonths, setColumnMonths] = useState([]);
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showCharts, setShowCharts] = useState(false);

  const fetchInvoiceSummary = useCallback((params = {}) => {
      setLoading(true);
      Apiauth({ ...params }, "Inventory/reports/invoiceSummary")
        .then((res) => {
          if (res?.code === 200) {
            const months = res.months ?? [];
            const data = res.data ?? [];
            setColumnMonths(months);
            setSummary(res.summary ?? res.totals ?? null);
            setFilter(res.filters ?? {});
            setGroups(mapApiGroups(months, data));
            const nextExpanded = {};
            data.forEach((row) => {
              nextExpanded[`g-${row.no}`] = true;
            });
            setExpanded(nextExpanded);
          }
        })
        .finally(() => {
          setLoading(false);
        });
  }, []);

  useEffect(() => {
    fetchInvoiceSummary();
  }, [fetchInvoiceSummary]);

  const handleFilterApply = useCallback(
    (filterParams = {}) => {
      fetchInvoiceSummary(filterParams);
    },
    [fetchInvoiceSummary]
  );

  const metrics = useMemo(() => mapMetricsFromSummary(summary), [summary]);
  const columnCount = columnMonths.length;

  const totalByMonth = useMemo(
    () => sumMappedGroupsByMonth(groups, columnCount),
    [groups, columnCount]
  );
  const totalNetByMonth = useMemo(
    () => sumNetQtyAndSalesByMonth(groups, columnCount),
    [groups, columnCount]
  );

  function toggleGroup(id) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <>
      <Header title="Invoice Summary" metrics={metrics} />
      <SubHeader
        filters={filter}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={handleFilterApply}
      />
      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <InvoiceChart groups={groups} />
      ) : (
        <div className="invoice-report-wrap">
          <div className="invoice-report-table-wrap">
            {columnCount === 0 ? (
              <div className="invoice-report-empty">{t("No invoice data for this period.")}</div>
            ) : (
              <table className="invoice-report-table">
                <thead>
                  <tr>
                    <th rowSpan={2} className="invoice-report-th">
                      #
                    </th>
                    <th rowSpan={2} className="invoice-report-th invoice-report-th--location">
                      {t("Location")}
                    </th>
                    {columnMonths.map((m) => (
                      <th key={m.key} colSpan={3} className="invoice-report-th">
                        {t(m.month_name)}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {columnMonths.map((m) => (
                      <React.Fragment key={`sub-${m.key}`}>
                        <th className="invoice-report-th">{t("Invoices")}</th>
                        <th className="invoice-report-th">{t("Qty")}</th>
                        <th className="invoice-report-th">{t("Sales")}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="invoice-report-tr--total border-handle">
                    <td className="invoice-report-td invoice-report-td--location border-handle" colSpan={2}>{t("Total")}</td>
                    <MonthCells
                      byMonth={Object.fromEntries(
                        Object.entries(totalByMonth).map(([monthIndex, values]) => [
                          monthIndex,
                          {
                            ...values,
                            qty: totalNetByMonth[monthIndex]?.qty ?? 0,
                            sales: totalNetByMonth[monthIndex]?.sales ?? 0,
                          },
                        ])
                      )}
                      columnCount={columnCount}
                    />
                  </tr>

                  {groups.map((g) => (
                    <React.Fragment key={g.id}>
                      <tr
                        className="invoice-report-tr--group"
                        onClick={() => toggleGroup(g.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleGroup(g.id);
                          }
                        }}
                      >
                        <td className="invoice-report-td invoice-report-td--index border-handle" style={{borderRight:'unset'}}>{t(g.index)}</td>
                        <td className="invoice-report-td invoice-report-td--location border-handle" style={{borderLeft:'unset'}}>
                          <span className="invoice-report-location-row">
                            <span>{t(g.label)}</span>
                            <span style={{ fontSize: 12, color: "#8b8b8b" }}>
                               {expanded[g.id] ? "▲" : "▼"}
                            </span>
                          </span>
                        </td>
                        <MonthCells byMonth={g.byMonth} columnCount={columnCount} />
                      </tr>
                      {expanded[g.id] &&
                        g.children.map((child,idx) => (
                          <tr key={`${g.id}-${child.id}`} className="invoice-report-tr--child border-handle">
                            <td className="invoice-report-td invoice-report-td--index border-handle" style={{borderRight:'unset',borderBottom: idx ===  g.children.length - 1 ? '1px solid #e5e7eb' : 'unset',background:'#f8fafc'}}/>
                            <td className="invoice-report-td invoice-report-td--child-indent border-handle" style={{borderLeft:'unset'}}>{t(child.label)}</td>
                            <MonthCells byMonth={child.byMonth} columnCount={columnCount} />
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Invoice;
